import { productEvent } from "@shared/analytics/productEvents";
import USDvIcon from "@shared/assets/logos/usdv-logomark.png";
import { SolomonLogomark } from "@shared/components/Brand/SolomonLogomark";
import {
    ArrowBracketUpIcon,
    BankIcon,
    CheckIcon,
    ChestIcon,
    ClockIcon,
    CoinIcon,
    GearIcon,
    LockIcon,
    PauseIcon,
    PencilPaperIcon,
    PlayIcon,
    PlusIcon,
    ProposeIcon,
    RetryIcon,
    ShieldCheckIcon,
    ShieldIcon,
    SnowflakeIcon,
    TagIcon,
    ThreeCoinsIcon,
    TriangleIcon
} from "@solomon-labs/design/components/icons";
import { Divider } from "@solomon-labs/design/components/layout";
import { ButtonIcon, Text } from "@solomon-labs/design/components/ui";
import {
    animate,
    motion,
    type MotionValue,
    useInView,
    useMotionValue,
    useMotionValueEvent,
    useReducedMotion,
    useTransform
} from "motion/react";
import React from "react";

import {
    AUTHORITY_CONTROL_FLOWS,
    type FlowGate,
    type JourneyAction,
    type JourneyStep,
    type SecurityFlow,
    type SecurityFlowIcon
} from "../securityControlFlows";

// Illustrative architecture, not live activity. Mint and Metadata are requested
// product labels, not assertions about verified multisig bindings. The PDA is
// a signing credential used by Chancery, never an independently running program.
interface SecurityRoute {
    id: string;
    label: string;
    authority: string;
    pda: string;
    request: string;
    outcome: string;
}
const ROUTES: readonly SecurityRoute[] = [
    {
        id: "pause",
        label: "Pause",
        authority: "Emergency",
        pda: "Pause PDA",
        request: "pause",
        outcome: "Transfers, minting, and burning paused."
    },
    {
        id: "unpause",
        label: "Unpause",
        authority: "Governance",
        pda: "Pause PDA",
        request: "unpause",
        outcome: "Transfers, minting, and burning resumed."
    },
    {
        id: "freeze",
        label: "Freeze",
        authority: "Enforcement",
        pda: "Freeze PDA",
        request: "freeze an account",
        outcome: "Selected USDv token account frozen."
    },
    {
        id: "thaw",
        label: "Thaw",
        authority: "Enforcement",
        pda: "Freeze PDA",
        request: "thaw an account",
        outcome: "Selected USDv token account thawed."
    },
    {
        id: "mint",
        label: "Mint",
        authority: "Mint",
        pda: "Mint PDA",
        request: "mint",
        outcome: "New USDv issued."
    },
    {
        id: "metadata",
        label: "Metadata",
        authority: "Metadata",
        pda: "Metadata PDA",
        request: "update metadata",
        outcome: "USDv metadata updated."
    }
];
function stepsFor(route: SecurityRoute): SecurityFlow["steps"] {
    const authorityIcon: SecurityFlowIcon =
        route.authority === "Emergency"
            ? "triangle"
            : route.authority === "Governance"
              ? "bank"
              : route.authority === "Enforcement"
                ? "shield"
                : route.authority === "Mint"
                  ? "coin"
                  : "tag";
    const pdaIcon: SecurityFlowIcon =
        route.pda === "Pause PDA"
            ? "pause"
            : route.pda === "Freeze PDA"
              ? "snowflake"
              : route.pda === "Mint PDA"
                ? "coin"
                : "tag";
    return [
        {
            label: route.authority,
            description: `Authorizes the request to ${route.request}.`,
            icon: authorityIcon
        },
        {
            label: "USDv Controls Program",
            description: "Checks the caller’s permissions.",
            icon: "chancery"
        },
        {
            label: route.pda,
            description:
                "The USDv Controls Program signs the call using this authority.",
            icon: pdaIcon
        },
        { label: "USDv", description: route.outcome, icon: "usdv" }
    ];
}
const ACTION_FLOWS: readonly SecurityFlow[] = ROUTES.map((route) => ({
    id: route.id,
    label: route.label,
    outcome: route.outcome,
    steps: stepsFor(route)
}));
interface Point {
    x: number;
    y: number;
}
// SVG illustration coordinates: the journey climbs from bottom left to top
// right along the ground plane. Move the stops, not the platform/glyph artwork.
const GRID_SPACING = 60;
const LEG_LENGTH = GRID_SPACING * 7;
const ISO_X = 0.82;
const ISO_Y = 0.482;
const GRID_WIDTH = GRID_SPACING * ISO_X * 2;
const GRID_HEIGHT = GRID_SPACING * ISO_Y * 2;
const STOPS: readonly Point[] = [
    { x: 0, y: 0 },
    { x: 0, y: -LEG_LENGTH },
    { x: 0, y: -LEG_LENGTH * 2 },
    { x: 0, y: -LEG_LENGTH * 3 }
];
// Three illustrative accounts fan out along the same isometric axes. Their
// labels fit together in the final frame; these are not a live asset inventory.
const DESTINATION_DISTANCE = GRID_SPACING * 4;
const DESTINATION_STOPS: readonly Point[] = [
    { x: DESTINATION_DISTANCE, y: -LEG_LENGTH },
    { x: 0, y: -LEG_LENGTH - DESTINATION_DISTANCE },
    { x: -DESTINATION_DISTANCE, y: -LEG_LENGTH }
];
const project = (point: Point): Point => ({
    x: (point.x - point.y) * ISO_X,
    y: (point.x + point.y) * ISO_Y
});
const DWELL = 2.2;
const MOVE = 1.4;
const GATE_DWELL = 1.3;
// Program operations appear one beat apart, then remain readable before travel.
const ACTION_BEAT = 1.1;
const ACTION_REVEAL = 0.25;
function dwellFor(step: JourneyStep): number {
    return DWELL + (step.actions?.length ?? 0) * ACTION_BEAT;
}
const ease = (value: number): number =>
    value < 0.5 ? 4 * value ** 3 : 1 - (-2 * value + 2) ** 3 / 2;
interface SegmentTiming {
    depart: number;
    arrive: number;
    gateStart?: number;
    gateEnd?: number;
    gate?: FlowGate;
}
interface FlowTiming {
    arrivals: number[];
    segments: SegmentTiming[];
    duration: number;
}
function timingFor(route: SecurityFlow): FlowTiming {
    const arrivals = [0];
    const segments: SegmentTiming[] = [];
    for (let index = 0; index < route.steps.length - 1; index++) {
        const gate = route.gates?.find(
            (candidate) => candidate.afterStep === index
        );
        const depart = arrivals[index] + dwellFor(route.steps[index]);
        const gateStart = gate ? depart + MOVE * gate.fraction : undefined;
        const gateEnd =
            gateStart === undefined ? undefined : gateStart + GATE_DWELL;
        const terminalBranch =
            route.destinations?.length && index === route.steps.length - 2;
        const arrive =
            depart +
            MOVE +
            (gate ? GATE_DWELL : 0) +
            (terminalBranch ? MOVE : 0);
        segments.push({ depart, arrive, gateStart, gateEnd, gate });
        arrivals.push(arrive);
    }
    return {
        arrivals,
        segments,
        duration:
            arrivals[arrivals.length - 1] +
            dwellFor(route.steps[route.steps.length - 1])
    };
}
function positionAt(time: number, timing: FlowTiming): Point {
    for (const [index, segment] of timing.segments.entries()) {
        if (time < segment.depart) return project(STOPS[index]);
        if (time <= segment.arrive) {
            const gate = segment.gate;
            let fraction: number;
            if (!gate)
                fraction = ease(Math.min(1, (time - segment.depart) / MOVE));
            else if (time < (segment.gateStart ?? 0)) {
                fraction =
                    ease(
                        Math.max(
                            0,
                            (time - segment.depart) / (MOVE * gate.fraction)
                        )
                    ) * gate.fraction;
            } else if (time < (segment.gateEnd ?? 0)) fraction = gate.fraction;
            else {
                fraction =
                    gate.fraction +
                    ease(
                        Math.min(
                            1,
                            (time - (segment.gateEnd ?? 0)) /
                                (MOVE * (1 - gate.fraction))
                        )
                    ) *
                        (1 - gate.fraction);
            }
            const from = STOPS[index];
            const to = STOPS[index + 1];
            return project({
                x: from.x + (to.x - from.x) * fraction,
                y: from.y + (to.y - from.y) * fraction
            });
        }
    }
    return project(STOPS[timing.arrivals.length - 1]);
}
function gateStateAt(
    time: number,
    segment: SegmentTiming
): "upcoming" | "waiting" | "released" {
    if (
        segment.gateStart === undefined ||
        segment.gateEnd === undefined ||
        time < segment.gateStart
    )
        return "upcoming";
    return time < segment.gateEnd ? "waiting" : "released";
}
const MAP_CSS = `
.security-map { --map-paper: rgb(var(--color-surface-ground)); --map-line: rgb(var(--color-line-base)); --map-tint-ground: rgb(var(--color-surface-tint-ground)); --map-tint-sunken: rgb(var(--color-surface-tint-sunken)); --map-inverse: rgb(var(--color-foreground-inverse)); --map-ember: rgb(var(--color-accent-base)); --map-neutral: rgb(var(--color-foreground-neutral)); }
.security-map .map-grid { stroke: var(--map-line); stroke-width: .8; opacity: .5; fill: none; }
.security-map .map-trace { stroke: var(--map-ember); stroke-width: 2; fill: none; }
.security-map .map-node-top { fill: var(--map-paper); }
.security-map .map-node-side { fill: rgb(var(--color-surface-sunken)); }
.security-map .map-inset { fill: var(--map-paper); }
.security-map .map-node-outline { fill: rgb(var(--color-foreground-muted)); }
.security-map .map-central { fill: var(--map-tint-ground); }
.security-map .map-central-side { fill: var(--map-tint-sunken); }
.security-map .map-central-inset-outline { fill: var(--map-inverse); }
.security-map .map-gate-mark { color: var(--map-neutral); transition: color .35s ease; }
.security-map .map-gate[data-state="waiting"] .map-gate-mark { color: var(--map-ember); }
.security-map .map-gate-caption { color: var(--map-neutral); transition: color .35s ease; }
.security-map .map-gate[data-state="waiting"] .map-gate-caption { color: var(--map-ember); }
@media (prefers-reduced-motion: reduce) { .security-map .map-gate-mark, .security-map .map-gate-caption { transition: none; } }
`;
function StageIcon({
    icon,
    emergencyFlashOpacity
}: {
    icon: SecurityFlowIcon;
    emergencyFlashOpacity?: MotionValue<number>;
}): React.ReactElement {
    if (icon === "chancery")
        return (
            <SolomonLogomark className="size-full text-foreground-inverse" />
        );
    if (icon === "usdv")
        return <img src={USDvIcon} alt="" className="size-full" />;
    if (icon === "triangle" && emergencyFlashOpacity)
        return (
            <motion.div
                className="map-emergency-icon flex size-full items-center justify-center"
                style={{ opacity: emergencyFlashOpacity }}
            >
                <TriangleIcon className="size-full text-error-base" />
            </motion.div>
        );
    const Icon = {
        "arrow-bracket-up": ArrowBracketUpIcon,
        pause: PauseIcon,
        "pencil-paper": PencilPaperIcon,
        snowflake: SnowflakeIcon,
        coin: CoinIcon,
        "three-coins": ThreeCoinsIcon,
        tag: TagIcon,
        triangle: TriangleIcon,
        bank: BankIcon,
        chest: ChestIcon,
        gear: GearIcon,
        shield: ShieldIcon
    }[icon];
    return <Icon className="size-full text-foreground-base" />;
}
const NODE_SCALE = 2.5;
const NODE_CENTER: Point = { x: 34.294, y: 20.256 };
// Exact node-base assembly from the docs UsdvFollowAnimation's
// NodeBaseBottom/NodeBaseTop asset (native 69×45 coordinate space).
function NodePlatform({
    step,
    layer,
    emergencyFlashOpacity
}: {
    step: JourneyStep;
    layer: "bottom" | "top";
    emergencyFlashOpacity?: MotionValue<number>;
}): React.ReactElement {
    const isChancery = step.icon === "chancery";
    const transform = `translate(${-NODE_CENTER.x * NODE_SCALE} ${-NODE_CENTER.y * NODE_SCALE}) scale(${NODE_SCALE})`;
    if (layer === "bottom") {
        return (
            <g transform={transform}>
                <polygon
                    points="68.4996,20.1753 34.4998,40.6752 0,20.1753 0,24.1751 34.0005,44.7641 68.5003,24.1758"
                    className={
                        isChancery
                            ? "map-node-side map-central-side"
                            : "map-node-side"
                    }
                />
                <g
                    transform="translate(0 19.8801)"
                    className={
                        isChancery
                            ? "map-node-outline map-central-side"
                            : "map-node-outline"
                    }
                >
                    <path d="M34.497 20.2122L68 0.0014782L68.5 0.29985L34.4998 20.7951L0 0.29985L0.5 0L34.497 20.2122Z" />
                    <path d="M68.4998 0.295166L68.5003 4.29567L34.0005 24.884L0 4.29497L0.000213669 0.295166L0.499256 0.579266V4.01397L34.0012 24.3005L67.9996 4.01047V0.577166L68.4998 0.295166Z" />
                </g>
            </g>
        );
    }
    return (
        <g transform={transform}>
            <polygon
                points="34.178,0 68.4996,20.1753 34.4998,40.6752 0,20.1753"
                className={
                    isChancery ? "map-node-top map-central" : "map-node-top"
                }
            />
            <path
                d="M17.7699 20.9245C16.5052 20.1254 16.5333 18.2714 17.8217 17.5111L31.9922 9.15495C33.5423 8.24078 35.464 8.2297 37.0241 9.12664L51.5115 17.4573C52.8014 18.1989 52.8489 20.0223 51.6455 20.8409L51.5253 20.9169L36.6243 29.639C35.1128 30.5236 33.2572 30.5496 31.7298 29.7274L31.4294 29.5513L17.7699 20.9245Z"
                className={isChancery ? "map-inset map-central" : "map-inset"}
            />
            <g>
                <path
                    d="M34.178 0L68.4996 20.1753L34.4998 40.6752L0 20.1753L34.178 0ZM34.497 40.0924L67.5218 20.1809L34.1766 0.580049L0.980558 20.1767L34.497 40.0924Z"
                    className={
                        isChancery
                            ? "map-node-outline map-central-side"
                            : "map-node-outline"
                    }
                />
                <path
                    d="M17.7699 20.9245C16.5052 20.1254 16.5333 18.2714 17.8217 17.5111L31.9922 9.15495C33.5423 8.24078 35.464 8.2297 37.0241 9.12664L51.5115 17.4573C52.8014 18.1989 52.8489 20.0223 51.6455 20.8409L51.5253 20.9169L36.6243 29.639C35.1128 30.5236 33.2572 30.5496 31.7298 29.7274L31.4294 29.5513L17.7699 20.9245ZM18.0758 17.942C17.1095 18.5123 17.0884 19.9033 18.0372 20.5025L31.6959 29.1287C33.1175 30.0265 34.9212 30.0568 36.3722 29.2074L51.2726 20.486C52.2667 19.9041 52.2608 18.4644 51.2622 17.8902L36.7755 9.5603C35.3714 8.75291 33.6408 8.76236 32.2456 9.58515L18.0758 17.942Z"
                    className={
                        isChancery
                            ? "map-node-outline map-central-inset-outline"
                            : "map-node-outline"
                    }
                />
            </g>
            <g transform="matrix(1.121 -0.66 1.121 0.659 20.83 19.32)">
                <foreignObject x={0} y={0} width={12} height={12}>
                    <div className="flex size-full items-center justify-center">
                        <StageIcon
                            icon={step.icon}
                            emergencyFlashOpacity={emergencyFlashOpacity}
                        />
                    </div>
                </foreignObject>
            </g>
        </g>
    );
}
function ProgramAction({
    action,
    index,
    clock,
    arrival,
    reducedMotion
}: {
    action: JourneyAction;
    index: number;
    clock: MotionValue<number>;
    arrival: number;
    reducedMotion: boolean;
}): React.ReactElement {
    const start = arrival + index * ACTION_BEAT;
    const opacity = useTransform(clock, (time) =>
        reducedMotion
            ? 1
            : Math.max(0, Math.min(1, (time - start) / ACTION_REVEAL))
    );
    const offset = useTransform(opacity, (value) => (1 - value) * 4);
    const Icon = {
        propose: ProposeIcon,
        clock: ClockIcon,
        check: CheckIcon,
        "shield-check": ShieldCheckIcon,
        plus: PlusIcon,
        pause: PauseIcon,
        snowflake: SnowflakeIcon
    }[action.icon];
    return (
        <motion.li
            className="flex items-center gap-2 text-foreground-muted"
            style={{ opacity, y: offset }}
            data-program-action={action.label}
            data-program-icon={action.icon}
        >
            <Icon className="size-5 shrink-0" />
            <Text variant="small" render={<span />}>
                {action.label}
            </Text>
        </motion.li>
    );
}
function StageObject({
    stage,
    step,
    layer,
    showDescription,
    destination,
    emergencyFlashOpacity,
    clock,
    arrival = 0,
    reducedMotion = false
}: {
    stage: number;
    step: JourneyStep;
    layer: "bottom" | "top";
    showDescription: boolean;
    destination?: number;
    emergencyFlashOpacity?: MotionValue<number>;
    clock?: MotionValue<number>;
    arrival?: number;
    reducedMotion?: boolean;
}): React.ReactElement {
    const point = project(
        destination === undefined
            ? STOPS[stage]
            : DESTINATION_STOPS[destination]
    );
    return (
        <g
            transform={`translate(${point.x} ${point.y})`}
            data-flow-stage={
                layer === "top" && destination === undefined
                    ? stage + 1
                    : undefined
            }
            data-flow-destination={
                layer === "top" && destination !== undefined
                    ? destination + 1
                    : undefined
            }
        >
            <NodePlatform
                step={step}
                layer={layer}
                emergencyFlashOpacity={emergencyFlashOpacity}
            />
            {layer === "top" && (
                <foreignObject
                    x={destination === undefined ? -120 : -90}
                    y={destination === undefined ? 80 : 68}
                    width={destination === undefined ? 240 : 180}
                    height={
                        showDescription && step.actions
                            ? 160
                            : showDescription && step.description
                              ? 90
                              : 40
                    }
                >
                    <div className="flex flex-col items-center gap-1 text-center">
                        <Text variant="body" className="text-foreground-base">
                            {step.label}
                        </Text>
                        {showDescription && step.description && (
                            <Text
                                variant="small"
                                className="text-foreground-muted"
                            >
                                {step.description}
                            </Text>
                        )}
                        {showDescription && step.actions && clock && (
                            <ul className="mt-1 flex list-none flex-col gap-2 p-0 text-left">
                                {step.actions.map((action, index) => (
                                    <ProgramAction
                                        key={`${action.icon}-${action.label}`}
                                        action={action}
                                        index={index}
                                        clock={clock}
                                        arrival={arrival}
                                        reducedMotion={reducedMotion}
                                    />
                                ))}
                            </ul>
                        )}
                    </div>
                </foreignObject>
            )}
        </g>
    );
}
function GateMarker({
    gate,
    state
}: {
    gate: FlowGate;
    state: "upcoming" | "waiting" | "released";
}): React.ReactElement {
    const from = STOPS[gate.afterStep];
    const to = STOPS[gate.afterStep + 1];
    const point = project({
        x: from.x + (to.x - from.x) * gate.fraction,
        y: from.y + (to.y - from.y) * gate.fraction
    });
    return (
        <g
            transform={`translate(${point.x} ${point.y})`}
            className="map-gate"
            data-flow-gate={gate.label}
            data-state={state}
        >
            <foreignObject x={-12} y={12} width={24} height={24}>
                <div className="flex size-full items-center justify-center">
                    <LockIcon className="map-gate-mark size-6" />
                </div>
            </foreignObject>
            <foreignObject x={16} y={12} width={210} height={24}>
                <div className="flex size-full items-center">
                    <Text
                        variant="fine-print"
                        className="map-gate-caption text-left"
                    >
                        {gate.label}
                    </Text>
                </div>
            </foreignObject>
        </g>
    );
}
function BranchSegment({
    clock,
    start,
    destination,
    layer
}: {
    clock: MotionValue<number>;
    start: number;
    destination: number;
    layer: "trace" | "traveller";
}): React.ReactElement {
    const from = project(STOPS[1]);
    const to = project(DESTINATION_STOPS[destination]);
    const progress = useTransform(clock, (time) =>
        ease(Math.max(0, Math.min(1, (time - start) / MOVE)))
    );
    const x = useTransform(
        progress,
        (fraction) => from.x + (to.x - from.x) * fraction
    );
    const y = useTransform(
        progress,
        (fraction) => from.y + (to.y - from.y) * fraction
    );
    const opacity = useTransform(clock, (time) => (time >= start ? 1 : 0));
    if (layer === "trace") {
        return (
            <motion.line
                x1={from.x}
                y1={from.y}
                x2={x}
                y2={y}
                className="map-trace"
                data-flow-branch={destination + 1}
            />
        );
    }
    return (
        <motion.g
            style={{ x, y, opacity }}
            data-flow-branch-traveller={destination + 1}
        >
            <path d="M -5 0 L 0 -3 L 5 0 L 0 3 Z" fill="var(--map-ember)" />
        </motion.g>
    );
}
function Journey({
    route,
    showDescriptions = Boolean(route.authority),
    showStageCounter = true
}: {
    route: SecurityFlow;
    showDescriptions?: boolean;
    showStageCounter?: boolean;
}): React.ReactElement {
    const steps = route.steps;
    const authorityView = Boolean(route.authority);
    const timing = React.useMemo(() => timingFor(route), [route]);
    const panel = React.useRef<HTMLDivElement>(null);
    const inView = useInView(panel, { amount: 0.4 });
    const reducedMotion = useReducedMotion();
    const [playing, setPlaying] = React.useState(true);
    const [activeStep, setActiveStep] = React.useState(0);
    const [finished, setFinished] = React.useState(false);
    const [replayKey, setReplayKey] = React.useState(0);
    const [branchPhase, setBranchPhase] = React.useState<
        "trunk" | "split" | "arrived"
    >("trunk");
    const [gateStates, setGateStates] = React.useState<
        readonly ("upcoming" | "waiting" | "released")[]
    >(route.gates?.map(() => "upcoming") ?? []);
    const clock = useMotionValue(0);
    // Three quick warning flashes follow the route clock, then the icon stays red.
    // This also pauses and replays with the route; reduced motion stays steady.
    const emergencyFlashOpacity = useTransform(clock, (time): number =>
        reducedMotion || time >= 1.2 || time % 0.4 < 0.2 ? 1 : 0.15
    );
    const branchStart = route.destinations?.length
        ? timing.arrivals[timing.arrivals.length - 1] - MOVE
        : undefined;
    const trunkOpacity = useTransform(clock, (time) =>
        branchStart === undefined || time < branchStart ? 1 : 0
    );
    const cameraX = useTransform(
        clock,
        (time) => 300 - positionAt(time, timing).x
    );
    const cameraY = useTransform(
        clock,
        (time) => 260 - positionAt(time, timing).y
    );
    const id = React.useId().replace(/:/g, "");
    const travelPoints = STOPS.slice(0, steps.length).map(project);
    const traveledX = useTransform(clock, (time) => positionAt(time, timing).x);
    const traveledY = useTransform(clock, (time) => positionAt(time, timing).y);
    useMotionValueEvent(clock, "change", (time) => {
        if (branchStart !== undefined) {
            setBranchPhase(
                time < branchStart
                    ? "trunk"
                    : time < branchStart + MOVE
                      ? "split"
                      : "arrived"
            );
        }
        setActiveStep(
            timing.arrivals.reduce(
                (last, arrival, index) =>
                    time + 0.001 >= arrival ? index : last,
                0
            )
        );
        if (route.gates?.length) {
            const nextStates = route.gates.map((gate) =>
                gateStateAt(time, timing.segments[gate.afterStep])
            );
            setGateStates((previous) =>
                nextStates.every((state, index) => state === previous[index])
                    ? previous
                    : nextStates
            );
        }
    });
    React.useEffect(() => {
        if (!playing || !inView || reducedMotion) return;
        const controls = animate(clock, timing.duration, {
            duration: Math.max(0, timing.duration - clock.get()),
            ease: "linear",
            onComplete: () => {
                setPlaying(false);
                setFinished(true);
            }
        });
        return (): void => controls.stop();
    }, [clock, playing, inView, reducedMotion, replayKey, timing]);
    function selectStep(index: number): void {
        setPlaying(false);
        setFinished(false);
        clock.set(timing.arrivals[index]);
        setActiveStep(index);
    }
    function replay(): void {
        setReplayKey((value) => value + 1);
        clock.set(0);
        setActiveStep(0);
        setFinished(false);
        setPlaying(true);
    }
    const playbackIcon = reducedMotion ? (
        activeStep === steps.length - 1 ? (
            <RetryIcon />
        ) : (
            <PlayIcon />
        )
    ) : finished ? (
        <RetryIcon />
    ) : playing ? (
        <PauseIcon />
    ) : (
        <PlayIcon />
    );
    const playbackLabel = reducedMotion
        ? activeStep === steps.length - 1
            ? "Restart route steps"
            : "Show next route stage"
        : finished
          ? "Replay route animation"
          : playing
            ? "Pause route animation"
            : "Resume route animation";
    function handlePlayback(): void {
        productEvent("stats:selected", {
            entry_point: "stats_page",
            section: "security_map",
            control: "playback",
            action: finished ? "replay" : playing ? "pause" : "play"
        });
        if (reducedMotion) {
            selectStep((activeStep + 1) % steps.length);
            return;
        }
        if (finished) {
            replay();
            return;
        }
        setPlaying((value) => !value);
    }
    const currentStep = steps[activeStep];
    const currentStepSummary = [
        currentStep.label,
        currentStep.description,
        currentStep.actions?.map((action) => action.label).join(", ")
    ]
        .filter(Boolean)
        .join(". ");
    return (
        <div ref={panel} className="min-w-0">
            <figure
                aria-label={`${route.label}: ${currentStepSummary}`}
                data-active-step={activeStep}
                data-flow-phase={
                    branchStart === undefined ? undefined : branchPhase
                }
            >
                <div className="relative aspect-square overflow-hidden bg-surface-ground">
                    <svg
                        viewBox="0 0 600 600"
                        className="h-full w-full"
                        aria-hidden="true"
                        focusable="false"
                    >
                        <defs>
                            <pattern
                                id={`${id}-grid`}
                                x={0}
                                y={0}
                                width={GRID_WIDTH}
                                height={GRID_HEIGHT}
                                patternUnits="userSpaceOnUse"
                            >
                                <path
                                    d={`M 0 0 L ${GRID_WIDTH} ${GRID_HEIGHT} M ${GRID_WIDTH} 0 L 0 ${GRID_HEIGHT}`}
                                    className="map-grid"
                                />
                            </pattern>
                        </defs>
                        <motion.g style={{ x: cameraX, y: cameraY }}>
                            <rect
                                x={-1800}
                                y={-1800}
                                width={5400}
                                height={3600}
                                fill={`url(#${id}-grid)`}
                            />
                            {travelPoints
                                .slice(0, activeStep)
                                .map((point, index) => (
                                    <path
                                        key={index}
                                        d={`M ${point.x} ${point.y} L ${travelPoints[index + 1].x} ${travelPoints[index + 1].y}`}
                                        className="map-trace"
                                    />
                                ))}
                            <motion.line
                                x1={travelPoints[activeStep].x}
                                y1={travelPoints[activeStep].y}
                                x2={traveledX}
                                y2={traveledY}
                                className="map-trace"
                            />
                            {route.destinations?.map((destination, index) => (
                                <BranchSegment
                                    key={destination.label}
                                    clock={clock}
                                    start={branchStart ?? 0}
                                    destination={index}
                                    layer="trace"
                                />
                            ))}
                            {route.destinations && (
                                <circle
                                    cx={travelPoints[travelPoints.length - 1].x}
                                    cy={travelPoints[travelPoints.length - 1].y}
                                    r={3}
                                    fill="var(--map-paper)"
                                    stroke="var(--map-ember)"
                                    strokeWidth={2}
                                    data-flow-branch-point
                                />
                            )}
                            {route.gates?.map((gate, index) => (
                                <GateMarker
                                    key={`${gate.afterStep}-${gate.label}`}
                                    gate={gate}
                                    state={gateStates[index] ?? "upcoming"}
                                />
                            ))}
                            {steps
                                .slice(0, route.destinations ? -1 : undefined)
                                .map((step, index) => (
                                    <StageObject
                                        key={index}
                                        stage={index}
                                        step={step}
                                        layer="bottom"
                                        showDescription={showDescriptions}
                                    />
                                ))}
                            {route.destinations?.map((step, index) => (
                                <StageObject
                                    key={step.label}
                                    stage={steps.length - 1}
                                    destination={index}
                                    step={step}
                                    layer="bottom"
                                    showDescription={showDescriptions}
                                />
                            ))}
                            {/* As in the docs: side band → traveller → opaque top face + glyph. */}
                            <motion.g
                                style={{
                                    x: traveledX,
                                    y: traveledY,
                                    opacity: trunkOpacity
                                }}
                                data-flow-traveller
                            >
                                <path
                                    d="M -5 0 L 0 -3 L 5 0 L 0 3 Z"
                                    fill="var(--map-ember)"
                                />
                            </motion.g>
                            {route.destinations?.map((destination, index) => (
                                <BranchSegment
                                    key={destination.label}
                                    clock={clock}
                                    start={branchStart ?? 0}
                                    destination={index}
                                    layer="traveller"
                                />
                            ))}
                            {steps
                                .slice(0, route.destinations ? -1 : undefined)
                                .map((step, index) => (
                                    <StageObject
                                        key={index}
                                        stage={index}
                                        step={step}
                                        layer="top"
                                        showDescription={showDescriptions}
                                        clock={clock}
                                        arrival={timing.arrivals[index]}
                                        reducedMotion={Boolean(reducedMotion)}
                                        emergencyFlashOpacity={
                                            step.emergency ||
                                            (authorityView &&
                                                route.id === "emergency" &&
                                                index === 0)
                                                ? emergencyFlashOpacity
                                                : undefined
                                        }
                                    />
                                ))}
                            {route.destinations?.map((step, index) => (
                                <StageObject
                                    key={step.label}
                                    stage={steps.length - 1}
                                    destination={index}
                                    step={step}
                                    layer="top"
                                    showDescription={showDescriptions}
                                />
                            ))}
                        </motion.g>
                    </svg>
                    {!authorityView && showStageCounter ? (
                        <div className="absolute right-5 top-5">
                            <Text
                                variant="fine-print"
                                className="text-foreground-muted"
                            >
                                {String(activeStep + 1).padStart(2, "0")} /{" "}
                                {String(steps.length).padStart(2, "0")}
                            </Text>
                        </div>
                    ) : null}
                    <div className="absolute bottom-5 right-5">
                        <ButtonIcon
                            variant="accent"
                            interaction="lifted"
                            size="lg"
                            className="[&_svg]:size-8"
                            icon={playbackIcon}
                            aria-label={playbackLabel}
                            onClick={handlePlayback}
                        />
                    </div>
                </div>
            </figure>
            <span className="sr-only" role="status" aria-live="polite">
                Step {activeStep + 1}: {currentStepSummary}
            </span>
        </div>
    );
}
/** Reuses the established isometric artwork and playback for documented examples. */
export function SecurityJourney({
    route,
    topLeft
}: {
    route: SecurityFlow;
    topLeft?: React.ReactNode;
}): React.ReactElement {
    return (
        <div className="security-map relative">
            <style dangerouslySetInnerHTML={{ __html: MAP_CSS }} />
            <Journey
                key={route.id}
                route={route}
                showDescriptions
                showStageCounter={false}
            />
            {topLeft && (
                <div className="absolute inset-x-3 top-3 z-10 flex justify-start sm:inset-x-5 sm:top-5">
                    {topLeft}
                </div>
            )}
        </div>
    );
}

export function SecurityMap({
    variant = "actions"
}: {
    variant?: "actions" | "authorities";
}): React.ReactElement {
    const routes =
        variant === "authorities" ? AUTHORITY_CONTROL_FLOWS : ACTION_FLOWS;
    const sectionId =
        variant === "authorities" ? "authority-control-map" : "security-map";
    const [routeId, setRouteId] = React.useState(routes[0].id);
    const route =
        routes.find((candidate) => candidate.id === routeId) ?? routes[0];
    return (
        <section
            className="security-map flex flex-col gap-8"
            aria-label={
                variant === "authorities"
                    ? "How Program Controls Work"
                    : undefined
            }
            aria-labelledby={
                variant === "actions" ? `${sectionId}-title` : undefined
            }
        >
            <style dangerouslySetInnerHTML={{ __html: MAP_CSS }} />
            {variant === "actions" && (
                <header>
                    <Text
                        variant="h1"
                        render={<h2 id={`${sectionId}-title`} />}
                    >
                        How Control Flows
                    </Text>
                </header>
            )}
            <div className="grid items-start gap-8 lg:grid-cols-5 lg:gap-12">
                <div
                    className="lg:col-span-2"
                    aria-label={
                        variant === "authorities"
                            ? "Program Authorities"
                            : "Security Actions"
                    }
                >
                    {routes.map((item) => {
                        const selected = item.id === route.id;
                        return (
                            <div
                                key={item.id}
                                className="border-t border-line-base last:border-b"
                            >
                                <h3>
                                    <button
                                        id={`${sectionId}-action-${item.id}`}
                                        type="button"
                                        aria-expanded={selected}
                                        aria-controls={`${sectionId}-panel-${item.id}`}
                                        onClick={() => {
                                            productEvent("stats:selected", {
                                                entry_point: "stats_page",
                                                section: variant,
                                                target: item.id
                                            });
                                            setRouteId(item.id);
                                        }}
                                        className="flex w-full items-center justify-between gap-3 py-5 text-left transition-colors duration-200 motion-reduce:transition-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-foreground-base"
                                    >
                                        <Text
                                            variant="body"
                                            render={<span />}
                                            className={
                                                selected
                                                    ? "text-accent-base"
                                                    : "text-foreground-muted"
                                            }
                                        >
                                            {item.label}
                                        </Text>
                                        <span
                                            aria-hidden="true"
                                            className={`size-1.5 ${selected ? "bg-accent-base" : "bg-line-base"}`}
                                        />
                                    </button>
                                </h3>
                                <div
                                    id={`${sectionId}-panel-${item.id}`}
                                    role="region"
                                    aria-labelledby={`${sectionId}-action-${item.id}`}
                                    hidden={!selected}
                                >
                                    <Text
                                        variant="small"
                                        className="pb-5 text-foreground-muted"
                                    >
                                        {item.outcome}
                                    </Text>
                                </div>
                            </div>
                        );
                    })}
                </div>
                <div className="min-w-0 lg:col-span-3">
                    <Journey key={route.id} route={route} />
                </div>
            </div>
            {variant === "authorities" && <Divider />}
        </section>
    );
}
